import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../utils/apiClient';

interface ActivityEntry {
  toolName: string;
  toolArgs?: string;
  success: boolean;
  errorMessage?: string;
  timestamp: number;
}

interface ActivityResponse {
  connectorId: string;
  clientName: string;
  entries: ActivityEntry[];
  hasMore: boolean;
}

export default function ConnectorActivityPage() {
  const { connectorId } = useParams<{ connectorId: string }>();
  const [data, setData] = useState<ActivityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!connectorId) {
      setLoading(false);
      setError('Missing connector ID');
      return;
    }
    setLoading(true);
    setError(null);
    setData(null);
    apiClient
      .get(`/api/mcp/connectors/${connectorId}/activity?limit=200`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'Failed to load activity');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [connectorId]);

  const formatTimestamp = (ts: number) => {
    const d = new Date(ts * 1000);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const relativeTime = (ts: number) => {
    const diff = Math.floor(Date.now() / 1000) - ts;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="page-shell">
      <div className="flex items-center gap-3 mb-6">
        <Link
          to="/settings"
          className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-ink-700 transition"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Settings
        </Link>
        <span className="text-ink-300">/</span>
        <span className="text-sm text-ink-700 font-medium">
          {data?.clientName || 'Connector'} Activity
        </span>
      </div>

      <div className="flex flex-col gap-3 mb-6">
        <h1 className="text-3xl font-semibold text-ink-700">
          {data?.clientName || 'Connector'} Activity Log
        </h1>
        <p className="text-sm text-ink-500">
          Tool calls made via this connector in the last 30 days.
        </p>
      </div>

      {loading ? (
        <div className="card p-8 text-center text-sm text-ink-500">Loading activity...</div>
      ) : error ? (
        <div className="card p-8 text-center text-sm text-red-600">{error}</div>
      ) : !data || data.entries.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-sm text-ink-500 mb-2">No activity recorded yet.</p>
          <p className="text-xs text-ink-400">
            Tool calls will appear here once this connector is used by an AI agent.
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-ink-100">
              <thead className="bg-ink-50/60">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-ink-500 uppercase tracking-wider">
                    When
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-ink-500 uppercase tracking-wider">
                    Tool
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-ink-500 uppercase tracking-wider">
                    Arguments
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-ink-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-50">
                {data.entries.map((entry, i) => (
                  <tr key={`${entry.timestamp}-${i}`} className="hover:bg-ink-50/40 transition">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <p className="text-sm text-ink-700">{relativeTime(entry.timestamp)}</p>
                      <p className="text-xs text-ink-400">{formatTimestamp(entry.timestamp)}</p>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <code className="text-sm font-mono text-ink-700 bg-ink-50 px-2 py-0.5 rounded">
                        {entry.toolName === 'auth' ? 'Connection' : entry.toolName}
                      </code>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      {entry.toolArgs ? (
                        <code className="text-xs text-ink-500 break-all line-clamp-2">
                          {entry.toolArgs}
                        </code>
                      ) : (
                        <span className="text-xs text-ink-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {entry.success ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded-full">
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          OK
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 px-2 py-1 rounded-full" title={entry.errorMessage}>
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Error
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.hasMore && (
            <div className="px-4 py-3 bg-ink-50/40 text-center text-xs text-ink-500">
              Showing the most recent entries. Older entries are automatically removed after 30 days.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
